import { ContextMenu } from '@/components/common';
import { ColumnsProps } from '@/Helper';
import { AlarmDetailsExpandedState } from '@/model/AlarmModel';
import { SelectOptions } from '@/model/CommonModel';
import { useStoreDispatch } from '@/store';
import { setShowAlarmDetails } from '@/store/alarm/actions';
import { ReadOutlined } from '@ant-design/icons';
import cx from 'classnames';
import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { AlarmTableDataSourceModel, instructionsStyle } from '../helpers';
import { columnKeys, getColumnValue } from './ExpandableRow';
import styles from './virtualTableExpandableRow.module.scss';

type Props = {
	data: AlarmTableDataSourceModel;
	index: number;
	rowColumnsWidth: CSSProperties;
	windowWidth: number;
	columns: ColumnsProps<AlarmTableDataSourceModel>[];
	alwaysShowInstructions: boolean;
	alarmStackingEnabled: boolean;
	isExpandableRow: boolean;
	setSize: (index: number, size: number) => void;
	handleOnToggleItemActive: (itemIndex: number) => void;
	contextMenuOptions?: SelectOptions<number>[];
	onClickContextMenuOption?: (key: number) => void;
	onRightClickRow?: (checked: boolean, activeAlarmId: number, isExpandableRowHeader: boolean, isExpanded: boolean) => void;
	style: React.CSSProperties;
};

const component: React.FC<Props> = ({
	data,
	index,
	setSize,
	windowWidth,
	columns,
	rowColumnsWidth,
	alwaysShowInstructions,
	isExpandableRow,
	handleOnToggleItemActive,
	contextMenuOptions,
	onClickContextMenuOption,
	onRightClickRow,
	style,
}: Props): JSX.Element => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const rowRef = useRef<HTMLDivElement>(undefined);
	//#endregion

	useEffect(() => {
		const { current } = rowRef;
		if (current) {
			setSize(index, current.getBoundingClientRect().height);
		}
	}, [setSize, index, windowWidth]);

	const onMouseUpHandled = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (event.button === 2) {
			const [alarmDetail] = data.AlarmDetails;
			if (!alarmDetail.IsChecked) {
				onRightClickRow(true, alarmDetail.ActiveAlarmId, alarmDetail.IsExpandableRowHeader, data.IsExpanded);
			}
		}
	};

	const createRow = () => {
		let returnComponent: JSX.Element = null;
		const [alarmDetail] = data.AlarmDetails;
		const showInstructions: boolean = data.Instructions !== '' && alwaysShowInstructions;
		let instructions: string = data.Instructions;
		let showMore: boolean = true;
		const instructionsData = data.HeaderAlarmDetails?.length > 0 ? { ...data, AlarmDetails: [alarmDetail] } : data;
		const rowSelectedClassName: string = alarmDetail?.IsChecked ? styles.rowSelected : '';
		const actionProps: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> } = {
			onMouseUp: onMouseUpHandled,
			ref: rowRef,
		};

		returnComponent = (
			<div
				className={cx(styles.rowBase, styles.row, rowSelectedClassName)}
				style={rowColumnsWidth}
				{...(!showInstructions ? actionProps : {})}
				onDoubleClick={() => {
					dispatch(setShowAlarmDetails({ alarmModel: instructionsData, showAlarmDetailsExpandedState: AlarmDetailsExpandedState.Both }));
				}}>
				{columns.map((column: ColumnsProps<AlarmTableDataSourceModel>, columnIndex: number) => (
					<div
						onDoubleClick={column.dataIndex === columnKeys.ActionsColumn ? e => e.stopPropagation() : undefined}
						key={`header-${index}-${columnIndex}`}
						className={cx(styles.cell, rowSelectedClassName, {
							[styles.cellNoPadding]: column.key === 'ActionHeader' || column.key === 'CollapseIconHeader',
							[styles.cellBorder]: !showInstructions,
						})}
						onClick={column.dataIndex === columnKeys.CollapseIcon && isExpandableRow ? () => handleOnToggleItemActive(index) : undefined}
						style={{
							justifyContent: column.align,
						}}>
						{
							column.render(
								getColumnValue({
									column: column.dataIndex,
									alarmDetail,
									data,
									isHeaderRow: isExpandableRow,
									alwaysShowInstructions,
								}),
								data,
								columnIndex
							) as ReactNode
						}
					</div>
				))}
			</div>
		);

		if (showInstructions) {
			const breakSpace: string[] = instructions.split('\r\n\r\n');
			if (breakSpace.length > 1) {
				instructions = breakSpace[0];
			} else if (instructions.length > 151) {
				instructions = instructions.substring(0, 149);
			} else {
				showMore = false;
			}

			returnComponent = (
				<div className={styles.rowBase} {...actionProps}>
					{returnComponent}
					<div className={cx(styles.rowInstruction, rowSelectedClassName)} style={rowColumnsWidth}>
						<div />
						<div className={styles.instructionCell}>
							{(!isExpandableRow || (isExpandableRow && data.IsExpanded)) &&
								columns.length > 0 &&
								(columns[1].render(
									getColumnValue({
										column: columns[1].dataIndex,
										alarmDetail,
										data,
										isHeaderRow: isExpandableRow,
										isInstructionRow: true,
										alwaysShowInstructions,
									}),
									data,
									0
								) as ReactNode)}
						</div>
						<div />
						<div className={cx(styles.instruction, rowSelectedClassName)} style={{ gridColumn: `4/${columns.length}` }}>
							<ReadOutlined style={instructionsStyle} />
							{instructions}
							{showMore && (
								<>
									&hellip;&nbsp;
									<span
										className={styles.showMore}
										onClick={() =>
											dispatch(
												setShowAlarmDetails({
													alarmModel: instructionsData,
													showAlarmDetailsExpandedState: AlarmDetailsExpandedState.Instructions,
												})
											)
										}>
										{_('ShowMore')}
									</span>
								</>
							)}
						</div>
						<div className={cx(styles.cell)} style={{ gridColumn: `${columns.length}/-1` }} />
					</div>
				</div>
			);
		}

		return returnComponent;
	};

	const enableContextMenu: boolean = data.AlarmDetails[0].IsChecked;

	return (
		<div className={styles.rowContainer} style={style}>
			{enableContextMenu ? (
				<ContextMenu options={contextMenuOptions} onClickOption={onClickContextMenuOption}>
					{createRow()}
				</ContextMenu>
			) : (
				createRow()
			)}
		</div>
	);
};

const Row = React.memo(component);
Row.displayName = 'AlarmRow';

export { Row };
